import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {Subscription} from 'rxjs';
import * as moment from 'moment';
import {environment} from '#env';
import {TranslateService} from '@ngx-translate/core';
import {Buffer} from 'buffer';
import {RequestsService} from './requests.service';
import {Response} from '#defs';
import * as Noty from 'noty';

@Injectable()
export class UtilService implements OnDestroy {
  get isMobile() {
    return this._isMobile;
  }

  set isMobile(value) {
    this._isMobile = value;
  }

  private _isMobile;
  private breakpointSub: Subscription;

  /**
   * Handle server error responses.
   * Used by other services to uniformly handle server error responses.
   */
  static handleErrorResponse(error: HttpErrorResponse): any {
    if (_.isEqual(error.status, -1)) {
      return 'Connection error. Please check your connection and try again.';
    }

    // TODO: handle 500 internal server error
    if (_.isString(error.error)) {
      return error.error;
    }

    if (_.isString(error)) {
      return error;
    }

    return 'There was an error processing your request. Please contact us.';
  }

  constructor(private breakpointObserver: BreakpointObserver,
              private translate: TranslateService,
              private requests: RequestsService) {
      this.breakpointSub = breakpointObserver.observe([Breakpoints.HandsetPortrait])
      .subscribe(({matches}) => this.isMobile = matches);
      this.translate.addLangs(['en', 'fil', 'ms']);
  }

  ngOnDestroy(): void {
    if (this.breakpointSub) {
      this.breakpointSub.unsubscribe();
    }
  }

  getRandom(max) {
    return Math.random() * max;
  }

  rfc4122v4() {
    let id = '', i;

    for (i = 0; i < 36; i++) {
      if (i === 14) {
        id += '4';
      } else if (i === 19) {
        id += '89ab'.charAt(this.getRandom(4));
      } else if (i === 8 || i === 13 || i === 18 || i === 23) {
        id += '-';
      } else {
        id += '0123456789abcdef'.charAt(this.getRandom(16));
      }
    }
    return id;
  }

  isVendorOpenForSlot(d: number, day: { openToday: boolean, openingTime: string, closingTime: string, day: number }) {
    const opening = day.openingTime.split(':');
    const closing = day.closingTime.split(':');

    // is same as opening or between
    const currentDate = moment().format('YYYY-MM-DD') + ' ' + this.pad(d, 2) + ':00:00';
    const openingTime = moment().format('YYYY-MM-DD') + ' ' + day.openingTime + ':00';
    const closingTime = moment().add(opening[0] > closing[0] ? 1 : 0, 'day').format('YYYY-MM-DD') + ' ' + day.closingTime + ':00';
    const sameOrBetween = moment(currentDate).isBetween(moment(openingTime), moment(closingTime), 'minutes', '[)');

    const midnight = (opening[0] > closing[0]
      && (
        moment(moment(new Date()).format('YYYY-MM-DD') + ' ' + this.pad(d, 2) + ':00:00').isBetween(
          moment(moment(new Date()).format('YYYY-MM-DD') + ' 00:00:00'),
          moment(moment(new Date()).format('YYYY-MM-DD') + ' ' + day.closingTime + ':00'), 'minutes', '[)')
      )
    );

    return sameOrBetween || midnight;
  }

  pad(value, size) {
    let s = String(value);
    while (s.length < (size || 2)) {
      s = '0' + s;
    }
    return s;
  }

  updateSlotsBasedOnPreOrderTime(preorderTime, selectedDate, slots) {
    if (preorderTime !== 0 && (selectedDate === undefined || moment().utcOffset(+480).isSame(moment(selectedDate), 'day'))) {
      const currentSlots = slots[moment(selectedDate).utcOffset(+480).weekday()];
      if (!currentSlots) {
        return;
      }
      const crntHrsPlusPreOrderTime = moment().utcOffset(+480).add(preorderTime, 'hours');
      return _.filter(currentSlots, (slot) => {
        return crntHrsPlusPreOrderTime.isBefore(
          moment(moment().format('YYYY-MM-DD') + ' ' + slot.start + slot.m1, 'YYYY-MM-DD hh:mm a').utcOffset(+480));
      });
    }
    return slots[moment(selectedDate).weekday()];
  }

  /**
   * Get difference between objects.
   * @param obj
   * @param base
   */
  difference(obj, base) {
    const __this = this;
    const diff = _.transform(obj, function(result, value, key) {
      if (!_.isEqual(value, base[key])) {
        // append parent object _id if available, so to track back the change
        if (obj['_id']) {
          result['_id'] = obj['_id'];
        }

        if (_.isObject(value) && _.isObject(base[key])) {
          result[key] = __this.difference(value, base[key]);
        } else {
          result[key] = value;
        }
      }
    }) as any[];

    // remove empty values from array
    _.remove(diff, v => _.isEmpty(v));

    return diff;
  }

  getCurrency(iso = 2) {
    const {iso2, iso3} = environment.currency;
    return iso === 2 ? iso2 : iso3;
  }

  setLanguage(lang){
    this.translate.use(lang);
  }

  getTranslation(key, param: any = "") {
    let translations;
    this.translate.get(key, param).subscribe((res: string) => {
      translations = res;
    })
    return translations;
  }

  getEstPickup(distanceDuration, vehicleType, service, pax, supplier) {
    distanceDuration = distanceDuration ? distanceDuration : 15; // default 30 minutes
    const mod = distanceDuration % 5;
    distanceDuration += +(mod ? 5 - mod : 0);
    if (!supplier.deliveryFeeBaseTypes || supplier.deliveryFeeBaseTypes.length === 0) {
      // if the time is less than 10 then we don't need to add 15.
      distanceDuration += 30;
      return distanceDuration;
    }
    if (service === 'vendor') {
      return distanceDuration += 30;
    }
    let sortedDeliveryFeeBaseTypes = (supplier.deliveryFeeBaseTypes).sort((a, b) => a.appliesAtPax - b.appliesAtPax);
    let resolvedDeliveryFeeBaseType = 0;
    for (let i = 0; i < sortedDeliveryFeeBaseTypes.length; i++) {
      const {appliesAtPax, vehicle} = sortedDeliveryFeeBaseTypes[i];

      if (pax <= appliesAtPax) {
        resolvedDeliveryFeeBaseType = environment.smartLogistic[`${vehicle}CapTime`];
        break;
      }
    }

    if (!resolvedDeliveryFeeBaseType && sortedDeliveryFeeBaseTypes.length > 0) {
      resolvedDeliveryFeeBaseType = environment.smartLogistic[`${sortedDeliveryFeeBaseTypes[sortedDeliveryFeeBaseTypes.length - 1].vehicle}CapTime`];
    }
    distanceDuration += resolvedDeliveryFeeBaseType || 30;

    return distanceDuration;
  }

  downloadSmartCanteenChildOrder(item: any, size: string) {
    item.downloadSmartCanteenOrder = true;
    this.requests.downloadSmartCanteenChildOrder(item.order._id, size).subscribe((response: Response) => {
        item.downloadSmartCanteenOrder = false;
        if (!response.success) {
          return new Noty({
            type: 'warning',
            theme: 'relax',
            text: response.error.description,
            timeout: 5000
          }).show();
        }

        const scodBuffer = Buffer.from(response['data']['scodBuffer']);
        const scodName = response['data']['scodName'];

        // create a temporary DOM Element and auto click to download, then remove
        const blob = new Blob([scodBuffer], {type: 'application/octet-stream'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'block';
        a.style.display = 'none';
        a.href = url;
        a.download = scodName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);

      },
      err => {
        item.downloadSmartCanteenOrder = false;
        return new Noty({
          type: 'warning',
          theme: 'relax',
          text: err.error.error.description,
          timeout: 5000
        }).show();
      });
  }

  canCancelDelivery(order): boolean {
    const lastDelivery = this.lastDelivery(order);
    return lastDelivery?.serviceProvider === 'vendor' || order?.serviceProvider === 'vendor';
  }

  lastDelivery(order) {
    return _.first(_.orderBy(order?.deliveryHistory || [], ['timestamp'], ['desc']));
  }

  lastDeliveryStatus(order, lastDelivery): string {
    return !_.isEmpty(lastDelivery) ? _.replace(lastDelivery?.status || '', /_/g, ' ') :
      order.deliveryOrderStatus ? _.replace(order.deliveryOrderStatus || '', /_/g, ' ') :
        '-';
  }

  isOnTime(order, lastDelivery, lastDeliveryStatus): boolean {
    return _.isUndefined(order.isOnTime) ?
      !_.isEmpty(lastDelivery) && (lastDeliveryStatus === 'delivered' || order.deliveryOrderStatus === 'delivered' || lastDeliveryStatus === 'finished' || order.deliveryOrderStatus === 'finished') ?
        moment(lastDelivery.timestamp).isBefore(moment(order.dailyMenuForDateEnd)) : false
      : order.isOnTime;
  }
}
